
import React, { useState, useRef, useEffect } from 'react';
import { Brush, Loader2 } from 'lucide-react';
import { DetectFurnitureMasks } from '../../../Services/Enhance';
import './remove-object-v2.css';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';

interface MaskData {
    id: number;
    class: string;
    text: string;
    maskBase64: string;
    color: string;
}

interface EnhanceObj {
    id: string;
    img_org: string;
    img_remove: string;
}

export default function RemoveObject({ image, onEnhancedImage }: { image: any; onEnhancedImage?: (img: EnhanceObj) => void }) {
    // Snackbar state
    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [message, setMessage] = useState('');
    const [colorSnackbar, setColorSnackbar] = useState<'success' | 'error' | 'info' | 'warning'>('info');

    const handleCloseSnackbar = (_event?: React.SyntheticEvent | Event, reason?: string) => {
        if (reason === 'clickaway') return;
        setOpenSnackbar(false);
    };
    const [originalImage, setOriginalImage] = useState<string | null>(image?.src || null);
    const [masks, setMasks] = useState<MaskData[]>([]);
    // For grouped selection: selectedLabel is the label (e.g. 'bed'), selectedMasks is the array of masks for that label
    const [selectedLabel, setSelectedLabel] = useState<string | null>(null);
    const [selectedMasks, setSelectedMasks] = useState<MaskData[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isRemoving, setIsRemoving] = useState(false);
    const [loadingMessage, setLoadingMessage] = useState('');
    const [brushSize, setBrushSize] = useState(20);
    const [brushMode, setBrushMode] = useState<'add' | 'erase'>('add');
    const [isDrawing, setIsDrawing] = useState(false);
    const [showHelp, setShowHelp] = useState(false);
    const [mode, setMode] = useState<'auto' | 'manual'>('auto');
    const canvasRef = useRef<HTMLCanvasElement | null>(null);
    const maskCanvasRef = useRef<HTMLCanvasElement | null>(null);
    const imageRef = useRef<HTMLImageElement | null>(null);
    const [selectionMode, setSelectionMode] = useState<'group' | 'single' | null>(null);
    const [showInfoCard, setShowInfoCard] = useState(false);


    // Call API to detect objects using image.src
    const detectObjects = async (image: string) => {
        setIsLoading(true);
        setLoadingMessage('Analyzing');
        try {
            const requestData = {
                data: [{
                    id: 0,
                    src: image
                }]
            };

            const apiResponse = await DetectFurnitureMasks(requestData);

            // L'API retourne un tableau, extraire le premier élément
            if (!apiResponse || !Array.isArray(apiResponse) || apiResponse.length === 0) {
                setMessage('No objects detected in the image.');
                setColorSnackbar('error');
                setOpenSnackbar(true);
                setIsLoading(false);
                return;
            }

            const data = apiResponse[0]; // Prendre le premier élément du tableau

            if (!data.objects || data.objects.length === 0) {
                setMessage('No objects detected in the image.');
                setColorSnackbar('error');
                setOpenSnackbar(true);
                setIsLoading(false);
                return;
            }

            // Process masks and decode base64
            const processedMasks = data.objects.map((obj: any, index: number) => ({
                id: index,
                class: obj.class,
                text: obj.text,
                maskBase64: obj.mask
            }));
            setMasks(processedMasks);
            setLoadingMessage('');
        } catch (error) {
            setMessage('Failed to detect objects');
            setColorSnackbar('error');
            setOpenSnackbar(true);
        } finally {
            setIsLoading(false);
        }
    };

    // Auto-detect objects on component mount
    useEffect(() => {
        if (image && image.src) {
            setOriginalImage(image.src);
            detectObjects(image.src);
        } else {
            console.warn('No image or image.src available');
        }
    }, []);

    // Draw image on canvas
    useEffect(() => {
        if (originalImage && canvasRef.current) {
            const canvas = canvasRef.current;
            const ctx = canvas.getContext('2d', { willReadFrequently: true });
            const img = new Image();

            img.crossOrigin = 'anonymous';

            img.onload = () => {
                canvas.width = img.width;
                canvas.height = img.height;
                if (ctx) {
                    ctx.drawImage(img, 0, 0);
                }
                imageRef.current = img;

                // Initialize mask canvas
                if (maskCanvasRef.current) {
                    maskCanvasRef.current.width = img.width;
                    maskCanvasRef.current.height = img.height;
                }

                // Generate previews for all masks
                if (masks.length > 0) {
                    generateObjectPreviews(img);
                }
            };

            img.src = originalImage;
        }
    }, [originalImage, masks]);

    // Group masks by label for UI
    const groupedMasks = React.useMemo(() => {
        const groups: Record<string, MaskData[]> = {};
        for (const mask of masks) {
            if (!groups[mask.text]) groups[mask.text] = [];
            groups[mask.text].push(mask);
        }
        return groups;
    }, [masks]);

    // Update canvas overlay when selection changes (do NOT clear maskCanvas, just add new masks)
    useEffect(() => {
        const maskCanvas = maskCanvasRef.current;
        if (!maskCanvas) return;

        const maskCtx = maskCanvas.getContext('2d', { willReadFrequently: true });
        if (!maskCtx) return;

        // Toujours vider le canvas avant de redessiner
        maskCtx.clearRect(0, 0, maskCanvas.width, maskCanvas.height);

        // MODE MANUAL → Ne jamais charger un mask automatique
        if (mode === 'manual') {
            refreshOverlay();
            return;
        }

        // MODE AUTO → On charge selon selectionMode
        (async () => {

            if (selectionMode === 'group' && selectedLabel) {
                const masksToDraw = groupedMasks[selectedLabel] || [];

                for (const mask of masksToDraw) {
                    try {
                        const img = await decodeMask(mask.maskBase64) as HTMLImageElement;

                        const tempCanvas = document.createElement('canvas');
                        tempCanvas.width = maskCanvas.width;
                        tempCanvas.height = maskCanvas.height;

                        const tempCtx = tempCanvas.getContext('2d', { willReadFrequently: true });
                        if (!tempCtx) continue;

                        tempCtx.drawImage(img, 0, 0, maskCanvas.width, maskCanvas.height);
                        const tempData = tempCtx.getImageData(0, 0, tempCanvas.width, tempCanvas.height);

                        // Copier pixels blancs seulemennt
                        for (let i = 0; i < tempData.data.length; i += 4) {
                            const r = tempData.data[i];
                            const g = tempData.data[i + 1];
                            const b = tempData.data[i + 2];

                            if (r > 128 && g > 128 && b > 128) {
                                const x = (i / 4) % maskCanvas.width;
                                const y = Math.floor((i / 4) / maskCanvas.width);
                                maskCtx.fillStyle = 'white';
                                maskCtx.fillRect(x, y, 1, 1);
                            }
                        }
                    } catch (err) {
                        console.error("Mask load failed:", err);
                    }
                }

                refreshOverlay();
                return;
            }

            if (selectionMode === 'single' && selectedMasks.length > 0) {
                const mask = selectedMasks[0]; // afficher le mask sélectionné uniquement

                try {
                    const img = await decodeMask(mask.maskBase64) as HTMLImageElement;

                    const tempCanvas = document.createElement('canvas');
                    tempCanvas.width = maskCanvas.width;
                    tempCanvas.height = maskCanvas.height;

                    const tempCtx = tempCanvas.getContext('2d', { willReadFrequently: true });
                    if (!tempCtx) return;

                    tempCtx.drawImage(img, 0, 0, maskCanvas.width, maskCanvas.height);
                    const tempData = tempCtx.getImageData(0, 0, tempCanvas.width, tempCanvas.height);

                    for (let i = 0; i < tempData.data.length; i += 4) {
                        const r = tempData.data[i];
                        const g = tempData.data[i + 1];
                        const b = tempData.data[i + 2];

                        if (r > 128 && g > 128 && b > 128) {
                            const x = (i / 4) % maskCanvas.width;
                            const y = Math.floor((i / 4) / maskCanvas.width);
                            maskCtx.fillStyle = 'white';
                            maskCtx.fillRect(x, y, 1, 1);
                        }
                    }
                } catch (err) {
                    console.error("Single mask load failed:", err);
                }

                refreshOverlay();
                return;
            }

            // Aucun mask applicable
            refreshOverlay();

        })();

    }, [selectedLabel, selectedMasks, selectionMode, groupedMasks, mode]);

    // Generate preview thumbnails for each label (use the first mask as preview)
    const generateObjectPreviews = async (img: HTMLImageElement) => {
        const previews: Record<string, string> = {};
        for (const label in groupedMasks) {
            const mask = groupedMasks[label][0];
            try {
                const maskImg = await decodeMask(mask.maskBase64) as HTMLImageElement;
                const preview = await createObjectPreview(img, maskImg);
                previews[label] = preview;
            } catch (error) {
                console.error('Error generating preview for label', label, error);
            }
        }
    };

    const refreshOverlay = () => {
        if (!canvasRef.current || !imageRef.current || !maskCanvasRef.current) return;

        const canvas = canvasRef.current;
        const ctx = canvas.getContext("2d", { willReadFrequently: true });
        const maskCanvas = maskCanvasRef.current;
        const maskCtx = maskCanvas.getContext("2d", { willReadFrequently: true });
        if (!ctx || !maskCtx) return;

        // 1. redraw original image
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(imageRef.current, 0, 0);

        // 2. build overlay from maskCanvas
        const imageData = maskCtx.getImageData(0, 0, maskCanvas.width, maskCanvas.height);
        const overlay = document.createElement("canvas");
        overlay.width = maskCanvas.width;
        overlay.height = maskCanvas.height;
        const overlayCtx = overlay.getContext("2d", { willReadFrequently: true });
        if (!overlayCtx) return;

        const overlayData = overlayCtx.createImageData(maskCanvas.width, maskCanvas.height);
        const data = imageData.data;

        const rgb = { r: 210, g: 0, b: 51 };

        for (let i = 0; i < data.length; i += 4) {
            const r = data[i], g = data[i + 1], b = data[i + 2];
            if (r > 128 && g > 128 && b > 128) {
                overlayData.data[i] = rgb.r;
                overlayData.data[i + 1] = rgb.g;
                overlayData.data[i + 2] = rgb.b;
                overlayData.data[i + 3] = 128;
            }
        }

        overlayCtx.putImageData(overlayData, 0, 0);
        ctx.drawImage(overlay, 0, 0);
    };

    // Create a cropped preview of the object
    const createObjectPreview = (originalImg: HTMLImageElement, maskImg: HTMLImageElement): Promise<string> => {
        return new Promise((resolve) => {

            const tempCanvas = document.createElement('canvas');
            tempCanvas.width = originalImg.width;
            tempCanvas.height = originalImg.height;
            const tempCtx = tempCanvas.getContext('2d', { willReadFrequently: true });

            // Draw original image
            if (tempCtx) {
                tempCtx.drawImage(originalImg, 0, 0);
            }

            // Get mask data
            const maskCanvas = document.createElement('canvas');
            maskCanvas.width = originalImg.width;
            maskCanvas.height = originalImg.height;
            const maskCtx = maskCanvas.getContext('2d', { willReadFrequently: true });
            if (!maskCtx) return resolve('');
            maskCtx.drawImage(maskImg, 0, 0, maskCanvas.width, maskCanvas.height);
            const maskData = maskCtx.getImageData(0, 0, maskCanvas.width, maskCanvas.height);

            // Find bounding box of the mask based on bright RGB pixels
            let minX = maskCanvas.width, minY = maskCanvas.height;
            let maxX = 0, maxY = 0;

            for (let y = 0; y < maskCanvas.height; y++) {
                for (let x = 0; x < maskCanvas.width; x++) {
                    const i = (y * maskCanvas.width + x) * 4;
                    const r = maskData.data[i];
                    const g = maskData.data[i + 1];
                    const b = maskData.data[i + 2];
                    if (r > 128 && g > 128 && b > 128) {
                        minX = Math.min(minX, x);
                        minY = Math.min(minY, y);
                        maxX = Math.max(maxX, x);
                        maxY = Math.max(maxY, y);
                    }
                }
            }

            // Add padding
            const padding = 10;
            minX = Math.max(0, minX - padding);
            minY = Math.max(0, minY - padding);
            maxX = Math.min(maskCanvas.width, maxX + padding);
            maxY = Math.min(maskCanvas.height, maxY + padding);

            const width = maxX - minX;
            const height = maxY - minY;

            // Create cropped preview
            const previewCanvas = document.createElement('canvas');
            const previewSize = 60;
            const scale = Math.min(previewSize / width, previewSize / height);
            previewCanvas.width = width * scale;
            previewCanvas.height = height * scale;
            const previewCtx = previewCanvas.getContext('2d', { willReadFrequently: true });
            if (!previewCtx) return resolve('');

            // Draw cropped and scaled image
            previewCtx.drawImage(
                tempCanvas,
                minX, minY, width, height,
                0, 0, previewCanvas.width, previewCanvas.height
            );

            resolve(previewCanvas.toDataURL());
        });
    };

    // Handle canvas click to select mask group (label)
    const handleCanvasClick = async (e: React.MouseEvent<HTMLCanvasElement>) => {
        if (!canvasRef.current || masks.length === 0 || isDrawing) return;
        setSelectionMode('single');
        const canvas = canvasRef.current;
        const rect = canvas.getBoundingClientRect();
        const scaleX = canvas.width / rect.width;
        const scaleY = canvas.height / rect.height;
        const x = Math.floor((e.clientX - rect.left) * scaleX);
        const y = Math.floor((e.clientY - rect.top) * scaleY);
        // Find which mask contains this point, then select its label
        for (const mask of masks) {
            const maskImg = await decodeMask(mask.maskBase64) as HTMLImageElement;
            const tempCanvas = document.createElement('canvas');
            tempCanvas.width = canvas.width;
            tempCanvas.height = canvas.height;
            const tempCtx = tempCanvas.getContext('2d', { willReadFrequently: true });
            if (!tempCtx) continue;
            tempCtx.drawImage(maskImg, 0, 0, canvas.width, canvas.height);

            const pixel = tempCtx.getImageData(x, y, 1, 1).data;
            if (pixel[0] > 128 && pixel[1] > 128 && pixel[2] > 128) {
                setSelectedLabel(mask.text);
                setSelectedMasks([mask]);
                break;
            }
        }
    };

    // Validate and decode base64 mask to image
    const decodeMask = (base64: string): Promise<HTMLImageElement> => {
        return new Promise((resolve, reject) => {
            // Basic validation: check for empty, non-string, or obviously invalid base64
            if (!base64 || typeof base64 !== 'string' || base64.length < 20) {
                console.warn('decodeMask: Invalid or empty base64 string:', base64);
                return reject(new Error('Invalid base64 mask'));
            }
            // Remove any data URL prefix if present
            let cleanBase64 = base64;
            if (base64.startsWith('data:image')) {
                const commaIdx = base64.indexOf(',');
                cleanBase64 = base64.substring(commaIdx + 1);
            }
            // Try to decode base64 to check validity
            try {
                // atob throws if not valid base64
                window.atob(cleanBase64);
            } catch (err) {
                console.error('decodeMask: Cannot decode base64 value:', base64, err);
                return reject(new Error('Cannot decode base64 value for mask'));
            }
            const img = new Image();
            img.crossOrigin = 'anonymous';
            img.onload = () => resolve(img);
            img.onerror = (e) => {
                console.error('decodeMask: Image failed to load from base64:', base64, e);
                reject(new Error('Image failed to load from base64 mask'));
            };
            img.src = `data:image/png;base64,${cleanBase64}`;
        });
    };

    // Brush drawing handlers
    const handleMouseDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
        setIsDrawing(true);
        draw(e);
    };

    const handleMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
        if (!isDrawing) return;
        draw(e);
    };

    const handleMouseUp = () => {
        setIsDrawing(false);
    };

    const draw = (e: React.MouseEvent<HTMLCanvasElement>) => {
        const canvas = canvasRef.current;
        const maskCanvas = maskCanvasRef.current;
        if (!canvas || !maskCanvas || !imageRef.current) return;
        const rect = canvas.getBoundingClientRect();
        const scaleX = canvas.width / rect.width;
        const scaleY = canvas.height / rect.height;
        const x = (e.clientX - rect.left) * scaleX;
        const y = (e.clientY - rect.top) * scaleY;

        const maskCtx = maskCanvas.getContext('2d', { willReadFrequently: true });
        if (!maskCtx) return;

        // Sauvegarder l'état actuel
        maskCtx.save();

        // Mode 'add' ajoute au masque blanc, mode 'erase' efface du masque
        if (brushMode === 'add') {
            // Ajouter au masque - dessine en blanc
            maskCtx.globalCompositeOperation = 'source-over';
            maskCtx.fillStyle = 'white';
            maskCtx.beginPath();
            maskCtx.arc(x, y, brushSize, 0, Math.PI * 2);
            maskCtx.fill();
        } else if (brushMode === 'erase') {
            // Effacer du masque - supprime les pixels blancs
            maskCtx.globalCompositeOperation = 'destination-out';
            maskCtx.beginPath();
            maskCtx.arc(x, y, brushSize, 0, Math.PI * 2);
            maskCtx.fill();
        }

        // Restaurer l'état
        maskCtx.restore();

        // Redraw overlay with updated mask - redessiner l'image originale
        const ctx = canvas.getContext('2d', { willReadFrequently: true });
        if (!ctx) return;
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(imageRef.current, 0, 0);

        // Get updated mask data
        const imageData = maskCtx.getImageData(0, 0, maskCanvas.width, maskCanvas.height);
        const data = imageData.data;

        // Use the same fixed mask color (r,g,b) = (210, 0, 51)
        const rgb = { r: 210, g: 0, b: 51 };

        // Create overlay
        const overlayCanvas = document.createElement('canvas');
        overlayCanvas.width = maskCanvas.width;
        overlayCanvas.height = maskCanvas.height;
        const overlayCtx = overlayCanvas.getContext('2d', { willReadFrequently: true });
        if (!overlayCtx) return;
        const overlayData = overlayCtx.createImageData(maskCanvas.width, maskCanvas.height);

        for (let i = 0; i < data.length; i += 4) {
            const r = data[i];
            const g = data[i + 1];
            const b = data[i + 2];
            if (r > 128 && g > 128 && b > 128) {
                overlayData.data[i] = rgb.r;
                overlayData.data[i + 1] = rgb.g;
                overlayData.data[i + 2] = rgb.b;
                overlayData.data[i + 3] = 128;
            }
        }

        overlayCtx.putImageData(overlayData, 0, 0);
        ctx.drawImage(overlayCanvas, 0, 0);
    };


    const removeObjects = async () => {

        setIsLoading(true);
        setLoadingMessage('Removing object from image...');

        try {
            // Compose a masked image: original image with the selected object painted solid red (210,0,51)
            const maskCanvas = maskCanvasRef.current;
            const baseImage = imageRef.current;
            if (!maskCanvas || !baseImage) {
                throw new Error('Mask canvas or base image not available');
            }

            const composedCanvas = document.createElement('canvas');
            composedCanvas.width = baseImage.width;
            composedCanvas.height = baseImage.height;
            const composedCtx = composedCanvas.getContext('2d', { willReadFrequently: true });
            if (!composedCtx) {
                throw new Error('Failed to get canvas context');
            }

            // Draw original image first
            composedCtx.drawImage(baseImage, 0, 0);

            // Read mask data
            const maskCtx = maskCanvas.getContext('2d', { willReadFrequently: true });
            if (!maskCtx) {
                throw new Error('Failed to get mask canvas context');
            }
            const maskData = maskCtx.getImageData(0, 0, maskCanvas.width, maskCanvas.height);
            const maskPixels = maskData.data;

            // Read image data to modify
            const imageData = composedCtx.getImageData(0, 0, composedCanvas.width, composedCanvas.height);
            const imgPixels = imageData.data;
            // Target color (210,0,51)
            const targetR = 210;
            const targetG = 0;
            const targetB = 51;

            // 4. Apply the mask
            for (let i = 0; i < maskPixels.length; i += 4) {
                // maskPixels[i + 3] represents the Alpha (opacity)
                // If the pixel is not transparent (Alpha > 0), it means the user drew here.
                const alpha = maskPixels[i + 3];

                if (alpha > 0) {
                    // Force the pixel to #D20033
                    imgPixels[i] = targetR;     // R
                    imgPixels[i + 1] = targetG; // G
                    imgPixels[i + 2] = targetB; // B
                    imgPixels[i + 3] = 255;     // Set full opacity for the red mask
                }
            }

            composedCtx.putImageData(imageData, 0, 0);

            // 5. Export and Send
            const savedImage = composedCanvas.toDataURL('image/png');

            // Send to parent component
            const enhanceObj = {
                "id": "",
                "img_org": originalImage || '',
                "img_remove": savedImage
            };

            if (onEnhancedImage) {
                onEnhancedImage(enhanceObj);
            }

            setLoadingMessage('');
        } catch (error) {
            setMessage('Failed to remove object. Please try again.');
            setColorSnackbar('error');
            setOpenSnackbar(true);
        } finally {
            setIsLoading(false);
        }
    };

    // Clear selection
    const clearSelection = () => {
        setSelectedLabel(null);
        setSelectedMasks([]);
        if (canvasRef.current && imageRef.current) {
            const ctx = canvasRef.current.getContext('2d', { willReadFrequently: true });
            if (ctx) {
                ctx.drawImage(imageRef.current, 0, 0);
            }
        }
        if (maskCanvasRef.current) {
            const maskCtx = maskCanvasRef.current.getContext('2d', { willReadFrequently: true });
            if (maskCtx) {
                maskCtx.clearRect(0, 0, maskCanvasRef.current.width, maskCanvasRef.current.height);
            }
        }
    };

    return (
        <div className="remove-object-container">
            <div className="remove-object-content">
                <div className="row">
                    {/* Canvas Area */}
                    <div className='col-md-8'>
                        <div className="canvas-area">
                            {/* Loader overlay centré sur l'image/canvas */}

                            {isLoading && (
                                <div
                                    className="loading-overlay"
                                >
                                    <div className="loading-dialog">
                                        <div className="loading-content">
                                            <Loader2 className="loading-spinner" />
                                            <span className="loading-message">{loadingMessage}</span>
                                        </div>
                                    </div>
                                </div>
                            )}
                            <div className="canvas-wrapper">

                                {/* Top-right floating action buttons */}
                                {!isLoading && <div className="canvas-action-buttons">
                                    <button
                                        className={`brush-icon ${brushMode === 'add' ? 'active' : ''}`}
                                        onClick={() => setBrushMode('add')}
                                        title="Brush"
                                        style={{
                                            backgroundColor: brushMode === 'add' ? '#746AFF' : '#fff',
                                            color: brushMode === 'add' ? '#fff' : '#746AFF',
                                            border: brushMode === 'add' ? 'none' : '1px solid #fff',
                                        }}
                                    >
                                        <Brush width={22} height={22} color={brushMode === 'add' ? '#fff' : '#746AFF'} />
                                    </button>
                                    <button
                                        className={`brush-icon ${brushMode === 'erase' ? 'active' : ''}`}
                                        onClick={() => setBrushMode('erase')}
                                        title="Erase"
                                        style={{
                                            backgroundColor: brushMode === 'erase' ? '#746AFF' : '#fff',
                                            color: brushMode === 'erase' ? '#fff' : '#746AFF',
                                            border: brushMode === 'erase' ? 'none' : '1px solid #fff',
                                            marginLeft: 8,
                                        }}
                                    >
                                        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <rect x="3" y="17" width="18" height="4" rx="2" fill={brushMode === 'erase' ? '#fff' : '#746AFF'} stroke={brushMode === 'erase' ? '#fff' : '#746AFF'} />
                                            <path d="M16 3l5 5-12.5 12.5a2.121 2.121 0 0 1-3 0l-2-2a2.121 2.121 0 0 1 0-3L16 3z" stroke={brushMode === 'erase' ? '#fff' : '#746AFF'} />
                                        </svg>
                                    </button>
                                    <button
                                        className={`mode-btn`}
                                        onClick={clearSelection}
                                        title="Reset"
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                                            <g data-name="Refresh Time">
                                                <path d="M12 2a9.975 9.975 0 0 0-7 2.882V3a1 1 0 0 0-2 0v4a1 1 0 0 0 1 1h4a1 1 0 0 0 0-2H6.713A8 8 0 1 1 4 12a1 1 0 0 0-2 0A10 10 0 1 0 12 2z" style={{ fill: '#fff' }} />
                                            </g>
                                        </svg>
                                    </button>
                                </div>}
                                <canvas
                                    ref={canvasRef}
                                    onClick={(mode === 'auto' && selectedMasks.length === 0) ? handleCanvasClick : undefined}
                                    onMouseDown={handleMouseDown}
                                    onMouseMove={handleMouseMove}
                                    onMouseUp={handleMouseUp}
                                    onMouseLeave={handleMouseUp}
                                    className="main-canvas"
                                />
                                <canvas
                                    ref={maskCanvasRef}
                                    className="mask-canvas"
                                />
                            </div>
                        </div>
                    </div>
                    <div className='col-md-4'>
                        {/* Controls Panel */}
                        <div className="controls-panel">
                            <div className="tabs width-tabs">
                                <button
                                    className={`tab para-14 width-100-100 color-black bold-500 ${mode === 'auto' ? ' active' : ''}`}
                                    onClick={() => { setMode("auto"); setSelectedLabel(null); setSelectedMasks([]); }}>
                                    Auto Detect
                                </button>
                                <button
                                    disabled={isRemoving}
                                    className={`tab para-14 color-black width-100-100 bold-500 ${mode === 'manual' ? ' active' : ''}`}
                                    onClick={() => { setMode("manual"); setBrushMode('add'); }}
                                >
                                    Manual
                                </button>
                            </div>
                            <div className="modern-header-toolbar">
                                {mode === 'auto' ? (
                                    <p className='text-center para-14 color-64748B font-500'>Click on image to select objects, or select from the list below</p>
                                ) : (
                                    <p className='text-center para-14 color-64748B font-500'>Draw over the object you want to remove</p>
                                )}
                                {mode === 'auto' && <>
                                    <div className='title-18'>Detected Objects</div>
                                    {isLoading ?
                                        <div className='d-flex justify-content-center align-items-center'>
                                            <div className="spinner-border text-primary size-20" role="status">
                                                <span className="visually-hidden">Loading...</span>
                                            </div>
                                        </div>
                                        : <div className="option-btn-row">
                                            <div className="objects-list-horizontal">
                                                {Object.keys(groupedMasks).map((label) => (
                                                    <div
                                                        key={label}
                                                        className={`object-item ${(selectionMode === 'group' && selectedLabel === label) ? 'selected' : 'unselected'}`}
                                                        onClick={() => {
                                                            if (selectedLabel === label) {
                                                                setSelectedLabel(null);
                                                                setSelectedMasks([]);
                                                            } else {
                                                                setSelectedLabel(label);
                                                                setSelectedMasks(groupedMasks[label]);
                                                                setSelectionMode('group');
                                                            }
                                                        }}
                                                    >
                                                        <div className="object-item-content">
                                                            <div className="object-info">
                                                                <div className="object-info-row">
                                                                    <span className="object-name">{label}</span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                            {
                                                (Object.keys(groupedMasks).length === 0 || openSnackbar) &&
                                                <div className="sensitivity-desc">
                                                    No objects detected, Please switch to Manual Mode to continue.
                                                </div>
                                            }
                                        </div>}
                                </>}
                                <div style={{ minHeight: "30vh" }}>
                                    {!isLoading && mode === 'auto' && <div className="adjust-selection-box">
                                        <div className="adjust-selection-header">
                                            <Brush width={18} height={18} style={{ color: "#746AFF" }} />
                                            <span className="adjust-selection-title">Adjust Selection</span>
                                            <span
                                                className="info-card-btn-wrapper"
                                                onMouseEnter={() => setShowInfoCard(true)}
                                                onMouseLeave={() => setShowInfoCard(false)}
                                                style={{ position: 'relative', display: 'inline-block' }}
                                            >
                                                <button
                                                    type="button"
                                                    className="info-card-btn"
                                                    tabIndex={0}
                                                    aria-label="Show help"
                                                >
                                                    <svg width="18" height="18" viewBox="0 0 16 16" fill="none">
                                                        <circle cx="8" cy="8" r="7" stroke="#746AFF" strokeWidth="2" fill="none" />
                                                        <text x="8" y="11.5" textAnchor="middle" fontSize="11" fontWeight="bold" fill="#746AFF" fontFamily="Arial">i</text>
                                                    </svg>
                                                </button>
                                                {showInfoCard && (
                                                    <div className="info-card-popover">
                                                        <img src="/assets/brush-to-remove.gif" alt="Brush Example" className="info-card-img" />
                                                    </div>
                                                )}
                                            </span>
                                        </div>
                                        <div className="sensitivity-row">
                                            <span className="sensitivity-label">Sensitivity</span>
                                            <span className="sensitivity-value">{brushSize}%</span>
                                        </div>
                                        <input
                                            type="range"
                                            min="5"
                                            max="100"
                                            value={brushSize}
                                            onChange={(e) => setBrushSize(Number(e.target.value))}
                                            className="modern-brush-slider"
                                        />
                                        <div className="sensitivity-desc">
                                            Lower for precise edges, higher for softer removal
                                        </div>
                                    </div>
                                    }
                                    {mode === 'manual' && <div className="adjust-selection-box">
                                        <div className="adjust-selection-header">
                                            <Brush width={18} height={18} style={{ color: "#746AFF" }} />
                                            <span className="adjust-selection-title">Brush to Remove</span>
                                        </div>
                                        <div className="sensitivity-row">
                                            <span className="sensitivity-label">Sensitivity</span>
                                            <span className="sensitivity-value">{brushSize}%</span>
                                        </div>
                                        <input
                                            type="range"
                                            min="5"
                                            max="100"
                                            value={brushSize}
                                            onChange={(e) => setBrushSize(Number(e.target.value))}
                                            className="modern-brush-slider"
                                        />
                                        <div className="sensitivity-desc">
                                            Lower for precise edges, higher for softer removal
                                        </div>
                                    </div>}
                                    {mode === 'manual' && (
                                        <div className="brush-gif-container">
                                            <img src="/assets/brush-to-remove.gif" alt="Brush to Remove Example" className="brush-gif" />
                                        </div>
                                    )}
                                </div>

                            </div>
                        </div>
                    </div>
                </div>
                <div className="action-buttons action-buttons-custom">
                    <button
                        onClick={removeObjects}
                        className="button-dash back-746AFF color-FFFFFF  border-746AFF padd-15 width-auto"
                    >
                        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <rect x="3" y="17" width="18" height="4" rx="2" fill='#fff' stroke='#fff' />
                            <path d="M16 3l5 5-12.5 12.5a2.121 2.121 0 0 1-3 0l-2-2a2.121 2.121 0 0 1 0-3L16 3z" stroke='#fff' />
                        </svg>   Remove Object
                    </button>
                </div>
            </div>

            <Snackbar open={openSnackbar} onClose={handleCloseSnackbar} autoHideDuration={10000}>
                <Alert
                    onClose={handleCloseSnackbar}
                    severity={colorSnackbar || "info"}
                    variant="filled"
                    sx={{ width: '100%' }}
                >
                    {message}
                </Alert>
            </Snackbar>
        </div>
    );
}